import { createSignal, Match, onCleanup, Show, Switch } from "solid-js";
import { createInitialState, type DeckBuild, type PlayerId } from "../core";
import { createGameController, type GameController } from "../app/controller";
import { createGameStore } from "../app/store";
import { createAiRunner, type AiRunner } from "../app/ai";
import { createDraftRunner, type DraftBackend } from "../app/draftRunner";
import { randomDeck } from "../app/decks";
import { buildMode } from "../app/buildMode";
import { createRoom, storeSeat } from "../app/api";
import { GameScreen } from "./screens/GameScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { OnlineApp } from "./screens/OnlineApp";
import { ReplayScreen } from "./screens/ReplayScreen";
import { DraftScreen } from "./screens/DraftScreen";
import { AiSetupScreen, type AiStartConfig } from "./screens/AiSetupScreen";
import { TutorialCoach } from "./components/TutorialCoach";
import { TutorialIntro } from "./components/TutorialIntro";
import { Tooltip } from "./components/Tooltip";

type Route =
  | { kind: "home" }
  | { kind: "room"; roomId: string }
  | { kind: "replay"; gameId: string };

function parseRoute(): Route {
  const room = location.pathname.match(/^\/g\/([A-Za-z0-9_-]+)$/);
  if (room) return { kind: "room", roomId: room[1]! };
  const replay = location.pathname.match(/^\/r\/([A-Za-z0-9_-]+)$/);
  if (replay) return { kind: "replay", gameId: replay[1]! };
  return { kind: "home" };
}

export function App() {
  const [route, setRoute] = createSignal<Route>(parseRoute());
  const [aiMode, setAiMode] = createSignal(false);
  const [tutorial, setTutorial] = createSignal(false);

  const onPop = () => setRoute(parseRoute());
  window.addEventListener("popstate", onPop);
  onCleanup(() => window.removeEventListener("popstate", onPop));

  function navigate(path: string) {
    history.pushState({}, "", path);
    setRoute(parseRoute());
  }

  async function createOnline() {
    const result = await createRoom(undefined, buildMode());
    storeSeat(result.roomId, result);
    navigate(`/g/${result.roomId}`);
  }

  function onMatched(m: { roomId: string; seat: PlayerId; token: string }) {
    storeSeat(m.roomId, { roomId: m.roomId, seat: m.seat, token: m.token });
    navigate(`/g/${m.roomId}`);
  }

  return (
    <>
      <Switch>
        <Match when={route().kind === "room"}>
          <OnlineApp
            roomId={(route() as { roomId: string }).roomId}
            onExit={() => navigate("/")}
          />
        </Match>
        <Match when={route().kind === "replay"}>
          <ReplayScreen
            gameId={(route() as { gameId: string }).gameId}
            onExit={() => navigate("/")}
          />
        </Match>
        <Match when={tutorial()}>
          <TutorialApp onExit={() => setTutorial(false)} />
        </Match>
        <Match when={aiMode()}>
          <AiApp onExit={() => setAiMode(false)} />
        </Match>
        <Match when={true}>
          <HomeScreen
            onVsComputer={() => setAiMode(true)}
            onTutorial={() => setTutorial(true)}
            onPlayFriend={createOnline}
            onMatched={onMatched}
          />
        </Match>
      </Switch>
      <Tooltip />
    </>
  );
}

/**
 * Single-player flow: choose how to build (draft is the default, classic is the
 * alternative), then face the computer (seat 1). A draft adds a screen between
 * setup and the game where you and the AI pick from a shared pool; classic goes
 * straight from the deck builder into the game.
 */
function AiApp(props: { onExit: () => void }) {
  const [game, setGame] = createSignal<{ controller: GameController; runner: AiRunner } | null>(null);
  const [draft, setDraft] = createSignal<DraftBackend | null>(null);
  const [cfg, setCfg] = createSignal<AiStartConfig | null>(null);

  /** Spin up the live game from both seats' decks (seat 0 you, seat 1 computer). */
  function beginGame(c: AiStartConfig, you: DeckBuild, computer: DeckBuild) {
    const seed = Math.floor(Math.random() * 0xffffffff) >>> 0;
    const state = createInitialState({
      id: crypto.randomUUID(),
      seed,
      config: c.config,
      players: [
        { name: c.name, build: you },
        { name: "Computer", build: computer },
      ],
    });
    const store = createGameStore(state);
    // You are seat 0; no privacy curtain in single-player.
    const controller = createGameController(store, {
      mySeat: 0,
      useCurtain: false,
      settings: { hideHandsBetweenTurns: false },
    });
    const runner = createAiRunner(store, 1, { difficulty: c.difficulty });
    setDraft(null);
    setGame({ controller, runner });
  }

  function start(c: AiStartConfig) {
    setCfg(c);
    if (c.mode === "classic") {
      beginGame(c, c.build ?? {}, randomDeck(c.config.budget));
    } else {
      const seed = Math.floor(Math.random() * 0xffffffff) >>> 0;
      // beginGame runs from the runner's onComplete (an event handler / timer),
      // not a reactive effect — otherwise the controller's memos would be owned
      // by the effect and disposed when it re-runs, killing move/deploy
      // highlighting. (That was the draft-only "no targets shown" bug.)
      setDraft(createDraftRunner(seed, { aiSeat: 1, onComplete: (b) => beginGame(c, b[0], b[1]) }));
    }
  }

  function reset() {
    setGame(null);
    setDraft(null);
  }

  return (
    <Switch fallback={<AiSetupScreen onStart={start} onBack={props.onExit} />}>
      <Match when={game()}>
        {(g) => (
          <GameScreen
            controller={g().controller}
            onExit={reset}
            onHome={props.onExit}
            aiThinking={g().runner.thinking}
          />
        )}
      </Match>
      <Match when={draft()}>
        {(d) => (
          <DraftScreen backend={d()} names={[cfg()!.name, "Computer"]} onExit={reset} />
        )}
      </Match>
    </Switch>
  );
}

/**
 * The guided first game: jumps straight into a gentle classic match vs an easy
 * computer (you get a clear edge), with a coach banner that teaches the royalty
 * mechanic from the live game state. No setup or draft — just play and learn.
 */
function TutorialApp(props: { onExit: () => void }) {
  const seed = Math.floor(Math.random() * 0xffffffff) >>> 0;
  const state = createInitialState({
    id: crypto.randomUUID(),
    seed,
    players: [
      // A friendly, capable beginner deck for the learner...
      { name: "You", build: { pawn: 3, knight: 2, rook: 1, queen: 1 } },
      // ...against a deliberately thin computer army on the easiest setting.
      { name: "Computer", build: { pawn: 4, farmer: 1 } },
    ],
  });
  const store = createGameStore(state);
  const controller = createGameController(store, {
    mySeat: 0,
    useCurtain: false,
    settings: { hideHandsBetweenTurns: false },
  });
  const runner = createAiRunner(store, 1, { difficulty: "easy" });

  // Show the paged basics walkthrough first; the human moves first, so the game
  // simply waits behind it until "Start playing" dismisses the intro.
  const [intro, setIntro] = createSignal(true);

  return (
    <>
      <GameScreen
        controller={controller}
        onExit={props.onExit}
        onHome={props.onExit}
        aiThinking={runner.thinking}
      />
      <TutorialCoach controller={controller} />
      <Show when={intro()}>
        <TutorialIntro onStart={() => setIntro(false)} onExit={props.onExit} />
      </Show>
    </>
  );
}
